import { Select } from "antd";
import { addCustomTag, deleteCustomTag, getCustomTagsByJob } from "api/candidate.api";
import { SvgIcons } from "Icon/SvgIcons";
import React, { useEffect, memo, useState, useMemo, useRef } from "react";
import SelectCustomTags from "./SelectCustomTags";



interface CustomCandidateTagsProps {
    smallText?: boolean;
    tags: string[];
    jobId: number | string;
    candidateId: number | string;
}

const CustomCandidateTags: React.FC<CustomCandidateTagsProps> = memo(({ smallText, tags: initialTags = [], jobId, candidateId }) => {
    const [localTags, setLocalTags] = useState<string[]>([]);
    const [isAddingTag, setIsAddingTag] = useState(false);
    const [showAll, setShowAll] = useState(false);
    const [addTagsOpen, setAddTagsOpen] = useState(false);

    const validTags = useMemo(() =>
        localTags.filter(tag => tag.trim() !== ""),
        [localTags]
    );
    
    const hasTags = validTags.length > 0;
    const displayedTags = showAll ? validTags : validTags.slice(0, 4);
    const handleShowMore = () => setShowAll(true);

    const isInitialMount = useRef(true);

    useEffect(() => {
        if (isInitialMount.current) {
            const newTags = Array.isArray(initialTags)
                ? initialTags.filter(tag => tag?.trim())
                : [];
            setLocalTags(newTags);
            isInitialMount.current = false;
        }
    }, [initialTags]);

    const handleTagChange = async (newTags: string) => {
        if (!jobId || !candidateId) return;

        try {
            setIsAddingTag(true);

            // Check against localTags instead of validTags
            const isAdded = localTags.includes(newTags);

            if (!isAdded) {
                // Add new tag
                await addCustomTag({
                    candidate: Number(candidateId),
                    job: Number(jobId),
                    tag: newTags
                });
                // Update local state by adding the new tag
                setLocalTags(prev => [...prev, newTags]);
            } else {
                // Remove existing tag
                await deleteCustomTag({
                    candidate: Number(candidateId),
                    job: Number(jobId),
                    tag: newTags
                });
                // Update local state by filtering out the deleted tag
                setLocalTags(prev => prev.filter(tag => tag !== newTags));
            }
        } catch (error) {
            console.error("Tag update failed:", error);
            // Revert to previous valid tags on error
            setLocalTags(validTags);
        } finally {
            setIsAddingTag(false);
        }
    };


    const handleAddTagsToggle = () => {
        setAddTagsOpen(!addTagsOpen);
    };

    return (
        <>
            <ul className="gap-1 flex items-center flex-wrap mt-2.5">
                <li>
                    <p className={`text-text-quaternary font-semibold ${smallText ? "text-xs" : ""}`}>
                        Tags:
                    </p>
                </li>
                {
                    hasTags ? (
                        <>
                            {displayedTags.map((job: any, index: number) => (
                                <li
                                    key={index}
                                    className={`px-2.5 py-1.5 text-[#10B981] ${smallText ? "text-xs" : "text-sm"
                                        } font-medium bg-[#10B981]/5 rounded-full cursor-pointer`}
                                >
                                    {job}
                                </li>
                            ))}
                            {!showAll && localTags?.length > 4 && (
                                <li
                                    onClick={handleShowMore}
                                    className={`px-2.5 py-1.5 text-[#10B981] ${smallText ? "text-xs" : "text-sm"
                                        } font-medium bg-[#10B981]/5 rounded-full cursor-pointer`}
                                >
                                    +{localTags?.length - 4}
                                </li>
                            )}
                        </>
                    ) : <p className={`text-text-tertiary ${smallText ? "text-xs" : "text-sm"} italic`}>
                        No tags available
                    </p>
                }

                {addTagsOpen && jobId && <li className="ml-3">
                    <SelectCustomTags
                        handleTagChange={handleTagChange}
                        isAddingTag={isAddingTag}
                        validTags={validTags}
                        disabled={isAddingTag}
                        jobId={jobId}
                    />
                    
                </li>}
                <li className="ml-1 flex justify-center items-center h-full">
                    <p
                        className={`text-primary font-semibold flex text-center items-center cursor-pointer ${smallText ? "text-xs" : ""}`}
                        onClick={handleAddTagsToggle}
                    >
                        {addTagsOpen ? SvgIcons.crossArrow : hasTags ? SvgIcons.pencileSvg : SvgIcons.primaryPlusSvg}
                    </p>
                </li>
            </ul>
        </>
    );
});

export default CustomCandidateTags;